<template>
  <div>
    <img src="~/assets/images/integrations/stripe.png" class="max-h-6 max-w-10 opacity-85 mb-2 mt-4">

    <o-alert v-if="isStatusAuthError" show :title="$t('integration.needsReconnecting')" class="text-sm" variant="warning">
      <p>
        {{ $t('integration.stripe.reconnectInfo') }}
      </p>
    </o-alert>

    <o-alert v-if="isStatusSetupIncomplete" show :title="$t('integration.setupIncomplete')" class="text-sm" variant="warning">
      <p>
        {{ $t('integration.stripe.setupIncompleteInfo') }}
      </p>
    </o-alert>

    <template v-if="isStatusConnected">
      <div v-if="!loaded && !error" class="w-full text-center my-6">
        <o-loader :label="$t('integration.stripe.loading')" size="20px" class="mx-auto mb-3" />
        <p class="text-sm text-gray-700 dark:text-gray-200">
          {{ $t('integration.stripe.loading') }}
        </p>
      </div>

      <div v-if="error" class="w-full my-8 text-sm">
        <o-alert variant="danger" :title="error" show />
      </div>

      <div v-if="loaded" class="w-full mb-3 text-sm border rounded py-3 px-3 bg-white">
        <StripeElements
          v-slot="{ elements }"
          ref="elms"
          :stripe-key="stripeKey"
          :instance-options="instanceOptions"
          :elements-options="elementsOptions"
        >
          <StripeElement
            ref="card"
            type="card"
            :elements="elements"
            :options="cardOptions"
          />
        </StripeElements>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
export default {
  guid: '29bdebaccd9e452fac2be8963d3daba7',
}
</script>

<script setup lang="ts">
import { StripeElements, StripeElement } from 'vue-stripe-js'

import {
  OAlert,
} from '@/components/elements'

interface Props {
  integration: any
}

const props = defineProps<Props>()

const elms = ref<any>()
const card = ref<any>()
const instanceOptions = ref({})
const elementsOptions = ref({})
const cardOptions = ref({
  value: {
    postalCode: '',
  },
})
const loaded = ref(false)
const error = ref<string | false>(false)

const isStatusDisconnected = computed(() => props.integration.isStatusDisconnected)
const isStatusConnected = computed(() => props.integration.isStatusConnected)
const isStatusSetupIncomplete = computed(() => props.integration.isStatusSetupIncomplete)
const isStatusAuthError = computed(() => props.integration.isStatusAuthError)
const stripeKey = computed(() => props.integration.data.authData.stripePublishableKey)

onMounted(() => {
  // Load Stripe Web Payment SDK script if not already loaded
  if ((window as any).Stripe) {
    nextTick(() => {
      setup()
    })
  } else {
    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.src = 'https://js.stripe.com/v3/'
    document.body.appendChild(script)

    script.onload = () => {
      nextTick(() => {
        setup()
      })
    }
  }
})

// Get Stripe integration ready
const setup = () => {
  loaded.value = true
}

// Create stripe token
const process = async () => {
  const groupComponent = elms.value
  const cardComponent = card.value

  const cardElement = cardComponent.stripeElement

  const result = await groupComponent.instance.createToken(cardElement)

  if (result.token && result.token.id) {
    return { stripeToken: result.token.id }
  } else {
    error.value = result.error?.message
    throw new Error(result.error)
  }
}

defineExpose({ process })
</script>
